import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import {
  selectCodeBrand,
  selectPdpaConsent,
  selectSurveyScreening,
  selectUniqueCodeDraft,
} from '@/stores/selectors';
import {
  getQ7_1Options,
  readQ7_1Answers,
  writeQ7_1Answers,
} from '@/lib/q7_1Storage';
import { surveyPathAfterQ7_1 } from '@/config/codeBrand';
import { clearLbsSkippedQ7_2Answers } from '@/lib/q7_2Storage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import type { AppTranslations } from '@/locales';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

/** Match /otherbrands brand row: bold white, body-size (not panel 20px). */
const checkboxLabelClass =
  'flex cursor-pointer items-start gap-3 text-left text-base font-bold leading-snug text-white';

const checkboxClass =
  'mt-0.5 h-5 w-5 shrink-0 rounded border-2 border-white/50 bg-black/20 accent-[#c9a882] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80';

/** Display label for a canonical (EN) /7_1 option. */
function q7_1OptionLabel(option: string, t: AppTranslations['q7_1']): string {
  switch (option) {
    case 'Seen it at a store/outlet':
      return t.optionSeenAtStore;
    case 'Heard about it from someone I know':
    case 'Heard about it from someone i know':
      return t.optionHeardFromSomeone;
    case 'Seen it online':
      return t.optionSeenOnline;
    case 'Seen the pack/product before':
      return t.optionSeenPackBefore;
    case "Don't remember":
      return t.optionDontRemember;
    default:
      return option;
  }
}

export default function Q7_1() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const codeBrand = useAppSelector(selectCodeBrand);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);
  const isDice = codeBrand === 'Dice';

  const options = useMemo(() => getQ7_1Options(codeBrand), [codeBrand]);
  const [selected, setSelected] = useState<string[]>(() => readQ7_1Answers()?.selectedOutlets ?? []);
  const navigateAfterCheckRef = useRef(false);

  useEffect(() => {
    if (!navigateAfterCheckRef.current) {
      return;
    }
    navigateAfterCheckRef.current = false;
    if (codeBrand === 'LBS') {
      clearLbsSkippedQ7_2Answers();
    }
    navigate(surveyPathAfterQ7_1(codeBrand));
  }, [selected, navigate, codeBrand]);

  const selectOption = (label: string) => {
    setSelected((prev) => {
      const wasChecked = prev.includes(label);
      const next = wasChecked ? [] : [label];
      writeQ7_1Answers({ selectedOutlets: next });
      if (!wasChecked && next.length === 1) {
        navigateAfterCheckRef.current = true;
      }
      return next;
    });
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] min-h-0 flex-1 flex-col overflow-hidden">
        {/* pt not mt: margin on flex-1 child is clipped by overflow-hidden (cuts bottom radius) */}
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[7vh] pb-4">
          {/* Panel fills viewport; answers scroll inside so bottom radius stays visible */}
          <SurveyBrownPanelInset className="flex min-h-0 flex-1 flex-col">
            <SurveyBrownPanel className="!flex !h-full !min-h-0 !flex-col !overflow-hidden !px-4 !pt-4 !pb-4">
              <p className="m-0 shrink-0 break-words text-left text-[19px] font-bold text-white sm:text-[20px]">
                {isDice ? t.q7_1.diceQuestion : t.q7_1.question}
              </p>
              <div className="mt-[4.5rem] min-h-0 flex-1 overflow-y-auto sm:mt-[5.25rem]">
                <div className="flex flex-col gap-[1.875rem] pb-2 sm:gap-[2.25rem]">
                  {options.map((option) => {
                    const id = `q71-${option.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-']/g, '')}`;
                    const checked = selected.includes(option);
                    return (
                      <label key={option} htmlFor={id} className={checkboxLabelClass}>
                        <input
                          id={id}
                          type="checkbox"
                          checked={checked}
                          onChange={() => selectOption(option)}
                          className={checkboxClass}
                        />
                        <span>{q7_1OptionLabel(option, t.q7_1)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
